type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  /** 로그를 발생시킨 사용자 ID */
  userId?: string;
  /** 수행 중인 액션 (예: 'login', 'api_request') */
  action?: string;
  /** 추가 메타데이터 */
  metadata?: Record<string, unknown>;
}

interface LogEntry extends LogContext {
  timestamp: string;
  level: LogLevel;
  message: string;
}

/**
 * 구조화된 로깅 유틸리티
 *
 * 개발 환경에서는 콘솔에 출력하고,
 * 프로덕션 환경에서는 외부 서비스로 전송할 준비가 되어 있습니다.
 *
 * @example
 * ```ts
 * // 기본 사용
 * logger.info('User logged in', { userId: '123', action: 'login' });
 *
 * // 에러 로깅
 * logger.error('API request failed', error, {
 *   action: 'api_request',
 *   metadata: { url: '/api/users', status: 500 },
 * });
 * ```
 */
class Logger {
  private isDev = import.meta.env.DEV;

  private formatMessage(entry: LogEntry): string {
    const { level, message, action, metadata } = entry;
    const prefix = `[${level.toUpperCase()}]`;
    const actionStr = action ? ` [${action}]` : '';
    const metaStr = metadata ? ` ${JSON.stringify(metadata)}` : '';
    return `${prefix}${actionStr} ${message}${metaStr}`;
  }

  private log(level: LogLevel, message: string, context?: LogContext) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      ...context,
    };

    // 개발 환경: 콘솔 출력
    if (this.isDev) {
      const formattedMessage = this.formatMessage(entry);
      switch (level) {
        case 'error':
          console.error(formattedMessage, context?.metadata);
          break;
        case 'warn':
          console.warn(formattedMessage, context?.metadata);
          break;
        case 'debug':
          console.debug(formattedMessage, context?.metadata);
          break;
        default:
          console.log(formattedMessage, context?.metadata);
      }
    }

    // 프로덕션 환경: 에러만 외부 서비스로 전송
    // Sentry 등 외부 서비스 연동 시 여기에 추가
    if (!this.isDev && level === 'error') {
      this.sendToExternalService(entry);
    }
  }

  /**
   * 외부 로깅 서비스로 전송 (Sentry 등)
   * 현재는 placeholder - 필요시 구현
   */
  private sendToExternalService(entry: LogEntry) {
    // TODO: Sentry 또는 다른 로깅 서비스 연동
    // 예시:
    // Sentry.captureMessage(entry.message, {
    //   level: entry.level,
    //   extra: entry.metadata,
    // });

    // 현재는 프로덕션 콘솔에만 출력 (디버깅용)
    console.error('[Logger] Production error:', entry);
  }

  /** 디버그 레벨 로그 (개발 환경에서만 표시) */
  debug(message: string, context?: LogContext) {
    this.log('debug', message, context);
  }

  /** 정보 레벨 로그 */
  info(message: string, context?: LogContext) {
    this.log('info', message, context);
  }

  /** 경고 레벨 로그 */
  warn(message: string, context?: LogContext) {
    this.log('warn', message, context);
  }

  /** 에러 레벨 로그 (프로덕션에서 외부 서비스로 전송) */
  error(message: string, error?: Error | unknown, context?: LogContext) {
    const errorInfo =
      error instanceof Error
        ? { errorMessage: error.message, errorStack: error.stack }
        : { errorMessage: String(error) };

    this.log('error', message, {
      ...context,
      metadata: {
        ...context?.metadata,
        ...errorInfo,
      },
    });
  }
}

/** 전역 로거 인스턴스 */
export const logger = new Logger();
